import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  HrActionText,
  HrFlexContainer,
  HrGrey,
  HrParagraph,
  HrPrimary,
  HrRed,
  Separate
} from '@components/styles/hr-styles';
import { useTranslationWithPrefix } from '@/i18n/hooks';
import { OptionsGroup } from '@/vacancy_request/components/template-request-form/options-group';
import {
  DEFAULT_CUSTOM_FIELD_REQUIRED,
  optionsList
} from '@/vacancy_request/components/template-request-form/options-group/constants';
import { CustomFieldModal } from '@/vacancy_request/components/template-request-form/custom-fields/custom-field-modal';
import {
  CUSTOM_FIELD_TABS,
  MAX_CUSTOM_FIELDS_PER_TAB
} from '@/vacancy_request/components/template-request-form/custom-fields/constants';

/**
 * Cabecera de la fila de un campo custom: nombre, pregunta y las acciones.
 * Se pasa como `label` de OptionsGroup para que la fila quede alineada con la de los campos
 * fijos — el nivel obligatorio/opcional/deshabilitado se dibuja a la derecha, igual que ellos.
 */
const CustomFieldRowLabel = ({ name, label, onEdit, onRemove, disabled }) => {
  const { t } = useTranslationWithPrefix('requestVacancies.requestTemplate.form.customFields');

  return (
    <HrFlexContainer flexDirection='column' alignItems='flex-start' gap='4px' width='auto'>
      <HrFlexContainer flexWrap='nowrap' alignItems='center' gap='8px' width='auto'>
        <HrParagraph>{name}</HrParagraph>
        {!disabled && (
          <>
            <HrActionText onClick={onEdit} type='button' aria-label={t('row.edit')}>
              <span className='hr-Pencil' />
            </HrActionText>
            <HrActionText color={HrRed} colorHover={HrRed} onClick={onRemove} type='button' aria-label={t('row.remove')}>
              <span className='hr-Trash' />
            </HrActionText>
          </>
        )}
      </HrFlexContainer>
      <HrParagraph fontSizeSetting={-2} color={HrGrey}>
        {label}
      </HrParagraph>
    </HrFlexContainer>
  );
};

CustomFieldRowLabel.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired
};

/**
 * Campos custom de una pestaña de la plantilla.
 *
 * La forma interna es `customFields.<tab>[]`: un array por pestaña, que es la unidad sobre la que
 * se valida el tope de 5. Ni `tab` ni `order` se guardan en cada item — el primero está implícito
 * en la key y el segundo es el índice del array. Los deriva el adapter cuando se defina el modelo
 * (F2), así no hay dos fuentes de verdad que puedan divergir.
 *
 * Esta sección NO valida las definiciones: eso lo garantiza el modal (ver el invariante en el
 * spec). Acá sólo se agrega, edita, borra y se elige el nivel.
 */
export const CustomFieldsSection = ({ tab, disabled }) => {
  const { t } = useTranslationWithPrefix('requestVacancies.requestTemplate.form.customFields');
  const { control, getValues } = useFormContext();
  const { fields, append, remove, update } = useFieldArray({ control, name: `customFields.${tab}` });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingField, setEditingField] = useState(null);

  const hasReachedLimit = fields.length >= MAX_CUSTOM_FIELDS_PER_TAB;

  const openCreateModal = () => {
    setEditingIndex(null);
    setEditingField(null);
    setIsModalOpen(true);
  };

  const openEditModal = (index) => {
    setEditingIndex(index);
    setEditingField(getValues(`customFields.${tab}.${index}`));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingIndex(null);
    setEditingField(null);
  };

  const handleConfirm = (values) => {
    if (editingIndex === null) {
      append({ _id: null, required: DEFAULT_CUSTOM_FIELD_REQUIRED, ...values });
      return;
    }

    const current = getValues(`customFields.${tab}.${editingIndex}`) || {};
    update(editingIndex, { ...current, ...values });
  };

  return (
    <>
      {fields.map((field, index) => (
        <React.Fragment key={field.id}>
          <OptionsGroup
            name={`customFields.${tab}.${index}.required`}
            label={
              <CustomFieldRowLabel
                name={field.name}
                label={field.label}
                onEdit={() => openEditModal(index)}
                onRemove={() => remove(index)}
                disabled={disabled}
              />
            }
            options={optionsList}
            disabled={disabled}
          />
          <Separate margin='0' />
        </React.Fragment>
      ))}

      {!disabled && (
        <HrFlexContainer justifyContent='flex-start' padding='8px 0'>
          {hasReachedLimit ? (
            <HrParagraph fontSizeSetting={-2} color={HrGrey}>
              {t('limitReached', { max: MAX_CUSTOM_FIELDS_PER_TAB })}
            </HrParagraph>
          ) : (
            <HrActionText color={HrPrimary} onClick={openCreateModal} type='button'>
              {`+ ${t('addField')}`}
            </HrActionText>
          )}
        </HrFlexContainer>
      )}

      <CustomFieldModal
        isOpen={isModalOpen}
        field={editingField}
        onClose={closeModal}
        onConfirm={handleConfirm}
      />
    </>
  );
};

CustomFieldsSection.propTypes = {
  tab: PropTypes.oneOf(Object.values(CUSTOM_FIELD_TABS)).isRequired,
  disabled: PropTypes.bool
};

CustomFieldsSection.defaultProps = {
  disabled: false
};
